import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { MessageSquare, Flag, CheckCircle2, Send, FileEdit } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useAuthStore } from '@/store/authStore';
import {
  addAnnotation,
  type DSRAnnotationRow,
} from '@/services/db/dsrDetail';

const ANNOTATE_ROLES = ['supervisor', 'safety_manager', 'tenant_admin', 'platform_admin'];

const TYPE_CONFIG: Record<string, { icon: typeof MessageSquare; className: string; label: string }> = {
  comment: { icon: MessageSquare, className: 'bg-mozaic-blue/15 text-mozaic-blue', label: 'Comment' },
  flag: { icon: Flag, className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Flag' },
  approval: { icon: CheckCircle2, className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Approval' },
  amendment: { icon: FileEdit, className: 'bg-mozaic-blue/15 text-mozaic-blue', label: 'Amendment' },
};

interface AnnotationPanelProps {
  dsrId: string;
  tenantId: string;
  annotations: DSRAnnotationRow[];
  defaultType?: 'comment' | 'flag' | 'approval';
}

export default function AnnotationPanel({
  dsrId,
  tenantId,
  annotations,
  defaultType,
}: AnnotationPanelProps) {
  const user = useAuthStore((s) => s.user);
  const userRole = useAuthStore((s) => s.userRole);
  const queryClient = useQueryClient();

  const canAnnotate = !!userRole && ANNOTATE_ROLES.includes(userRole);

  const [body, setBody] = useState('');
  const [annotationType, setAnnotationType] = useState<'comment' | 'flag' | 'approval'>(
    defaultType ?? 'comment'
  );

  const mutation = useMutation({
    mutationFn: () =>
      addAnnotation(dsrId, {
        tenant_id: tenantId,
        annotation_type: annotationType,
        body: body.trim(),
        created_by: user!.id,
      }),
    onSuccess: (newAnnotation) => {
      // Optimistic-style: patch the cache immediately with the returned row
      queryClient.setQueryData(['dsr_detail', dsrId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          annotations: [...(old.annotations ?? []), newAnnotation],
        };
      });
      setBody('');
      setAnnotationType('comment');
    },
    onError: () => {
      // Refetch to get accurate state
      queryClient.invalidateQueries({ queryKey: ['dsr_detail', dsrId] });
    },
  });

  return (
    <div id="section-annotations" className="scroll-mt-20 space-y-4">
      <h2 className="text-base font-semibold">
        Annotations
        {annotations.length > 0 && (
          <Badge className="ml-2 bg-mozaic-navy/10 text-mozaic-navy">
            {annotations.length}
          </Badge>
        )}
      </h2>

      {/* Existing annotations */}
      {annotations.length === 0 && (
        <p className="py-2 text-sm text-muted-foreground">No annotations yet.</p>
      )}

      {annotations.map((a) => {
        const config = TYPE_CONFIG[a.annotation_type] ?? TYPE_CONFIG.comment;
        const Icon = config.icon;
        const authorName = a.users
          ? `${a.users.first_name} ${a.users.last_name}`
          : 'Unknown';

        return (
          <Card key={a.id} className="border-l-4" style={{ borderLeftColor: 'var(--border)' }}>
            <CardContent className="py-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{authorName}</span>
                <Badge className={config.className}>
                  <Icon className="mr-1 h-3 w-3" />
                  {config.label}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {format(parseISO(a.created_at), "MMM d, yyyy 'at' HH:mm")}
                </span>
              </div>
              <p className="mt-1.5 text-sm whitespace-pre-wrap">{a.body}</p>
            </CardContent>
          </Card>
        );
      })}

      {/* Add annotation form — supervisor+ only */}
      {canAnnotate && (
        <Card>
          <CardContent className="space-y-3 pt-4">
            <div className="flex gap-2">
              {(['comment', 'flag', 'approval'] as const).map((type) => {
                const config = TYPE_CONFIG[type];
                const Icon = config.icon;
                const isActive = annotationType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setAnnotationType(type)}
                    className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                      isActive
                        ? config.className
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-3 w-3" />
                    {config.label}
                  </button>
                );
              })}
            </div>

            <Textarea
              placeholder="Add an annotation..."
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 1000))}
              rows={3}
              className="resize-none"
            />

            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {body.length}/1000
              </span>
              <Button
                size="sm"
                disabled={!body.trim() || mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                <Send className="mr-1 h-3.5 w-3.5" />
                {mutation.isPending ? 'Submitting...' : 'Add Annotation'}
              </Button>
            </div>

            {mutation.isError && (
              <p className="text-xs text-destructive">
                Failed to add annotation. Please try again.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
